/**
 * Persisted user-preferences store (dark mode, per-interface config).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UserPreferences, InterfaceConfig } from '@/types/preferences.types';

/** Local cache of {@link UserPreferences} (dark mode + per-interface config). */
interface PreferencesStore {
  preferences: UserPreferences | null;
  isLoaded: boolean;
  /** Replace the cached preferences blob (called after login hydrate). */
  setPreferences: (prefs: UserPreferences) => void;
  /** Merge one interface config key (sidebar, table columns, …). */
  updateInterfaceConfig: (interfaceName: string, config: Record<string, unknown>) => void;
  updateDarkMode: (isDark: boolean) => void;
  /** Drop cache on logout. */
  clearPreferences: () => void;
  getInterfaceConfig: (interfaceName: string) => InterfaceConfig[string] | undefined;
}

/** Persisted preferences store; {@link AuthProvider} hydrates it after login. */
export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set, get) => ({
      preferences: null,
      isLoaded: false,

      setPreferences: (prefs: UserPreferences) => {
        set({ preferences: prefs, isLoaded: true });
      },

      updateInterfaceConfig: (interfaceName: string, config: Record<string, unknown>) => {
        set((state) => {
          if (!state.preferences) return state;

          return {
            preferences: {
              ...state.preferences,
              interfaceConfigs: {
                ...state.preferences.interfaceConfigs,
                [interfaceName]: config,
              },
            },
          };
        });
      },

      updateDarkMode: (isDark: boolean) => {
        set((state) => {
          if (!state.preferences) return state;

          return {
            preferences: {
              ...state.preferences,
              isDarkMode: isDark,
            },
          };
        });
      },

      clearPreferences: () => {
        set({ preferences: null, isLoaded: false });
      },

      getInterfaceConfig: (interfaceName: string) => {
        const state = get();
        if (!state.preferences) return undefined;
        return state.preferences.interfaceConfigs[interfaceName];
      },
    }),
    {
      name: 'ednov-preferences',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
